import {Modal} from '@mantine/core';
import React from 'react';
import type {DeviceRequest, DeviceResponse} from 'src/api/ProactApiClient';
import {isOfflineQueuedResult} from 'src/offline/offlineMutation';
import {newTempDeviceId} from 'src/app/inspections/offline/photoCapture';
import {DeviceForm} from 'src/pages/Modules/Inspections/shared/DeviceForm';
import {
  useAddCustomInspectionDeviceMutation,
  useGetInspectionCustomDeviceOptionsQuery,
} from 'src/redux/queries/inspectionRecord';

export interface CustomDeviceModalProps {
  opened: boolean;
  inspectionId: number;
  siteId: number;
  onClose: () => void;
  onCreated: (device: DeviceResponse) => void;
}

export const CustomDeviceModal: React.FC<CustomDeviceModalProps> = ({
  opened,
  inspectionId,
  siteId,
  onClose,
  onCreated,
}) => {
  const [addCustomDevice] = useAddCustomInspectionDeviceMutation();
  const {data: options, isLoading: isOptionsLoading} =
    useGetInspectionCustomDeviceOptionsQuery(inspectionId, {skip: !opened});

  const handleSubmitOverride = async (
    model: DeviceRequest
  ): Promise<DeviceResponse> => {
    const tempDeviceId = newTempDeviceId();
    const entry = await addCustomDevice({
      inspectionId,
      request: model,
      tempDeviceId,
    }).unwrap();

    // Offline-queued has no server id yet — navigate by the temp id the applier
    // appended to the scope snapshot; online reconstructs from the entry.
    const queued = isOfflineQueuedResult(entry);

    return {
      id: queued ? tempDeviceId : entry.deviceId,
      name: queued ? (model.name ?? '') : entry.deviceName,
      siteId: model.siteId,
      equipmentTypeId: model.equipmentTypeId,
      equipmentTypeName: queued ? undefined : entry.equipmentTypeName,
      serialNumber: model.serialNumber,
      building: model.building,
      floor: model.floor,
      room: model.room,
      manufacturer: model.manufacturer,
      model: model.model,
      installDate: model.installDate,
      active: true,
      isVerified: false,
    } as DeviceResponse;
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      title="Add Device"
      closeOnClickOutside={false}>
      {opened && (
        <DeviceForm
          mode="create"
          embedded
          lockedSiteId={siteId}
          onSubmitOverride={handleSubmitOverride}
          onCreated={onCreated}
          onCancelled={onClose}
          optionsOverride={{
            sites: options?.sites ?? [],
            equipmentTypes: options?.equipmentTypes ?? [],
            isLoading: isOptionsLoading,
          }}
        />
      )}
    </Modal>
  );
};
